import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  Alert,
} from '@mui/material';
import { dateToSqlDate } from '../../helpers/dateFuncs';
import fetchData from '../../helpers/fetchData';
import { formatNumber } from '../../helpers/numberUtils';
import { exportToExcel } from '../../helpers/exportFuncs';
import { getComparator, Order, sortedRowInformation } from '../../helpers/sortingsFuncs';
import LoadingSpinner from '../LoadingSpinner';
import { visuallyHidden } from '@mui/utils';

interface ComparisonData {
  datum: string;
  e_affiliate_id: string;
  anmeldungen: number;
  column_name: string;
  value_date1: number;
  value_date2: number;
  change_amount: number;
  change_percentage: number;
  network: string;
  theme: string;
  planungsbereich: string;
  newsletter?: string;
  [key: string]: any;
}

type DataKey = keyof ComparisonData;

interface SnapshotDateComparisonProps {
  network?: string[];
  theme?: string[];
  planungsbereich?: string[];
  column?: string;
  minChange?: number;
  leadStartDate?: Date | null;
  leadEndDate?: Date | null;
  availableDates?: string[];
}

interface HeaderMappingItem {
  name: string;
  type: string;
  digits: number;
  attach: string;
}

const detailsHeaderMapping: Record<string, HeaderMappingItem> = {
  datum: {
    name: 'Lead Datum',
    type: 'string',
    digits: 0,
    attach: '',
  },
  e_affiliate_id: {
    name: 'Affiliate ID',
    type: 'string',
    digits: 0,
    attach: '',
  },
  column_name: {
    name: 'Spalte',
    type: 'string',
    digits: 0,
    attach: '',
  },
  value_date1: {
    name: 'Wert Datum 1',
    type: 'number',
    digits: 2,
    attach: '',
  },
  value_date2: {
    name: 'Wert Datum 2',
    type: 'number',
    digits: 2,
    attach: '',
  },
  change_amount: {
    name: 'Änderung (€)',
    type: 'number',
    digits: 2,
    attach: '€',
  },
  change_percentage: {
    name: 'Änderung (%)',
    type: 'number',
    digits: 2,
    attach: '%',
  },
  network: {
    name: 'Netzwerk',
    type: 'string',
    digits: 0,
    attach: '',
  },
  theme: {
    name: 'Thema',
    type: 'string',
    digits: 0,
    attach: '',
  },
  planungsbereich: {
    name: 'Planungsbereich',
    type: 'string',
    digits: 0,
    attach: '',
  },
};

const truncateAffiliateId = (affiliateId: string): string => {
  if (affiliateId.length > 40) {
    return affiliateId.substring(0, 37) + '...';
  }
  return affiliateId;
};

const getRowColor = (changeAmount: number): string => {
  const absChange = Math.abs(changeAmount);
  if (changeAmount < 0 && absChange >= 10) return 'rgba(244, 67, 54, 0.1)'; // Red for large decreases
  if (changeAmount < 0 && absChange >= 1) return 'rgba(255, 152, 0, 0.1)'; // Orange for medium decreases
  if (changeAmount > 0 && absChange >= 10) return 'rgba(76, 175, 80, 0.1)'; // Green for large increases
  return 'transparent';
};

export const SnapshotDateComparison: React.FC<SnapshotDateComparisonProps> = ({
  network,
  theme,
  planungsbereich,
  column,
  minChange,
  leadStartDate,
  leadEndDate,
  availableDates = [],
}) => {
  const [date1, setDate1] = useState<string>('');
  const [date2, setDate2] = useState<string>('');
  const [data, setData] = useState<ComparisonData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<DataKey>('change_amount');

  const handleCompare = async () => {
    if (!date1 || !date2) {
      setError('Bitte wählen Sie beide Daten aus');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const options: any = {
        date1,
        date2,
      };

      if (network && network.length > 0) {
        options.network = network;
      }
      if (theme && theme.length > 0) {
        options.theme = theme;
      }
      if (planungsbereich && planungsbereich.length > 0) {
        options.planungsbereich = planungsbereich;
      }
      if (column && column !== 'all') {
        options.column = column;
      }
      if (minChange !== undefined && minChange > 0) {
        options.min_change = minChange;
      }
      if (leadStartDate) {
        options.lead_start_date = dateToSqlDate(leadStartDate);
      }
      if (leadEndDate) {
        options.lead_end_date = dateToSqlDate(leadEndDate);
      }

      const comparisonData = (await fetchData<Record<string, any>[]>(
        '/api/vnr/snapshots/compare',
        options,
      )) as Record<string, any>[];

      setData(
        comparisonData.map((item) => ({
          datum: item.datum,
          e_affiliate_id: item.e_affiliate_id,
          anmeldungen: parseInt(item.anmeldungen) || 0,
          column_name: item.column_name,
          value_date1: parseFloat(item.value_date1) || 0,
          value_date2: parseFloat(item.value_date2) || 0,
          change_amount: parseFloat(item.change_amount) || 0,
          change_percentage: parseFloat(item.change_percentage) || 0,
          network: item.network,
          theme: item.theme,
          planungsbereich: item.planungsbereich,
          newsletter: item.newsletter,
        })),
      );
      setLoading(false);
    } catch (err: any) {
      setError(err.message || 'Fehler beim Laden der Vergleichsdaten');
      setLoading(false);
    }
  };

  const handleRequestSort = (property: DataKey) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const createSortHandler = (property: DataKey) => () => {
    handleRequestSort(property);
  };

  const sortedData = sortedRowInformation<ComparisonData>(data, getComparator(order, orderBy));

  const headers = Object.keys(detailsHeaderMapping);

  return (
    <Box bgcolor="#fff" p={3}>
      <Typography variant="h6" gutterBottom>
        Snapshot Vergleich: Zwei Daten vergleichen
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}>
        <TextField
          label="Datum 1"
          type="date"
          value={date1}
          onChange={(e) => setDate1(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="Datum 2"
          type="date"
          value={date2}
          onChange={(e) => setDate2(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <Button variant="contained" onClick={handleCompare} disabled={loading}>
          Vergleichen
        </Button>
      </Box>

      {availableDates.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>Verfügbare Snapshot Daten:</strong> {availableDates.slice(0, 10).join(', ')}
          {availableDates.length > 10 && ` (und ${availableDates.length - 10} weitere)`}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && <LoadingSpinner />}

      {!loading && data.length > 0 && (
        <>
          <Box sx={{ mb: 2 }}>
            <Button
              onClick={() =>
                exportToExcel(sortedData, `VNR-Snapshot-Comparison-${date1}-vs-${date2}`)
              }
              sx={{ float: 'right' }}
            >
              Download als Excel
            </Button>
            <Box sx={{ clear: 'both', pt: 1 }}>
              <strong>Gefundene Änderungen:</strong> {data.length}
            </Box>
          </Box>
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }} size="small">
              <TableHead>
                <TableRow>
                  {headers.map((header) => (
                    <TableCell key={header}>
                      <TableSortLabel
                        active={orderBy === header}
                        direction={orderBy === header ? order : 'asc'}
                        onClick={createSortHandler(header as DataKey)}
                      >
                        {detailsHeaderMapping[header].name || header}
                        {orderBy === header ? (
                          <Box component="span" sx={{ ...visuallyHidden }}>
                            {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                          </Box>
                        ) : null}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedData.map((row, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      backgroundColor: getRowColor(row.change_amount),
                    }}
                  >
                    {Object.entries(detailsHeaderMapping).map(([key, value]) => (
                      <TableCell key={key}>
                        {key === 'e_affiliate_id' ? (
                          <span title={row[key] as string}>
                            {truncateAffiliateId(row[key] as string)}
                          </span>
                        ) : value.type === 'number' ? (
                          row[key] === null ? (
                            ''
                          ) : (
                            formatNumber(Number(row[key]), value.digits) + (value.attach || '')
                          )
                        ) : (
                          row[key]
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {!loading && data.length === 0 && date1 && date2 && !error && (
        <Alert severity="info">
          Keine Änderungen zwischen {date1} und {date2} gefunden
        </Alert>
      )}
    </Box>
  );
};

export default SnapshotDateComparison;
